import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar, User, Mail, Phone, Building2, FileText, Truck, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VermietungBuchen() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect base path: /atob-logistic/vermietung or /vermietung
  const basePath = useMemo(() => {
    const path = location.pathname;
    const buchenIdx = path.indexOf('/buchen/');
    if (buchenIdx > -1) return path.substring(0, buchenIdx);
    return '/vermietung';
  }, [location.pathname]);

  const backPath = basePath;
  const confirmPath = `${basePath}/bestaetigung`;
  const [vehicle, setVehicle] = useState(null);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Form state
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_company: '',
    notes: ''
  });

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/vehicles/${vehicleId}`).then(r => r.json()),
      fetch(`${API_URL}/api/vehicles/${vehicleId}/availability`).then(r => r.json())
    ])
      .then(([v, a]) => {
        setVehicle(v);
        setBookedRanges(a.booked_ranges || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Fahrzeug konnte nicht geladen werden.');
        setLoading(false);
      });
  }, [vehicleId]);

  const totalDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  }, [startDate, endDate]);

  const totalPrice = useMemo(() => {
    if (!vehicle || totalDays <= 0) return 0;
    return (vehicle.price_per_day * totalDays).toFixed(2);
  }, [vehicle, totalDays]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      setError('Bitte wählen Sie einen Mietzeitraum aus.');
      return;
    }
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setSubmitting(true);
    setError('');

    const formatDate = (d) => d.toISOString().split('T')[0];

    try {
      const res = await fetch(`${API_URL}/api/bookings/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicle_id: vehicleId,
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone,
          customer_company: form.customer_company || undefined,
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
          notes: form.notes || undefined,
          origin_url: window.location.origin,
          success_path: confirmPath,
          cancel_path: `${basePath}/buchen/${vehicleId}`
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Fehler bei der Buchung.');
        setSubmitting(false);
        return;
      }

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch {
      setError('Netzwerkfehler. Bitte versuchen Sie es erneut.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0F1A2E] border-t-transparent animate-spin"></div>
      </main>
    );
  }

  if (!vehicle) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-white/70">Fahrzeug nicht gefunden.</p>
      </main>
    );
  }

  return (
    <main data-testid="vermietung-buchen-page" className="pt-20">
      {/* Header */}
      <section className="bg-[#0F1A2E] py-16 md:py-20">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button
              onClick={() => navigate(backPath)}
              data-testid="back-to-vehicles"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6 text-sm uppercase tracking-wider"
            >
              <ChevronLeft size={16} />
              Zurück zur Übersicht
            </button>
            <div className="accent-line mb-6"></div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3">
              {vehicle.name}
            </h1>
            <p className="text-lg text-white/70">{vehicle.category} &middot; {vehicle.price_per_day.toFixed(0)}€ / Tag</p>
          </motion.div>
        </div>
      </section>

      {/* Booking Content */}
      <section className="section-spacing bg-[#0F1A2E]">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Left: Calendar + Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Calendar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-[#0A1220] p-6 md:p-8 border border-[#1e3a5f]"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Calendar size={20} className="text-[#F5A623]" />
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-white">
                    Mietzeitraum wählen
                  </h2>
                </div>
                <BookingCalendar
                  currentMonth={currentMonth}
                  setCurrentMonth={setCurrentMonth}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  bookedRanges={bookedRanges}
                />
                {startDate && endDate && (
                  <div className="mt-6 p-4 bg-[#0F1A2E]/5 border-l-4 border-[#0F1A2E]">
                    <p className="text-sm text-white/70">
                      <span className="font-semibold text-white">{totalDays} {totalDays === 1 ? 'Tag' : 'Tage'}</span>
                      {' '}&middot; {formatDisplayDate(startDate)} bis {formatDisplayDate(endDate)}
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-[#0A1220] p-6 md:p-8 border border-[#1e3a5f]"
              >
                <div className="flex items-center gap-3 mb-6">
                  <User size={20} className="text-[#F5A623]" />
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-white">
                    Ihre Angaben
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <FormField
                    icon={User}
                    label="Name *"
                    value={form.customer_name}
                    onChange={(v) => setForm(f => ({ ...f, customer_name: v }))}
                    placeholder="Max Mustermann"
                    testId="input-name"
                  />
                  <FormField
                    icon={Mail}
                    label="E-Mail *"
                    type="email"
                    value={form.customer_email}
                    onChange={(v) => setForm(f => ({ ...f, customer_email: v }))}
                    placeholder="max@beispiel.de"
                    testId="input-email"
                  />
                  <FormField
                    icon={Phone}
                    label="Telefon *"
                    type="tel"
                    value={form.customer_phone}
                    onChange={(v) => setForm(f => ({ ...f, customer_phone: v }))}
                    placeholder="+49 123 456 789"
                    testId="input-phone"
                  />
                  <FormField
                    icon={Building2}
                    label="Firma (optional)"
                    value={form.customer_company}
                    onChange={(v) => setForm(f => ({ ...f, customer_company: v }))}
                    placeholder="Firma GmbH"
                    testId="input-company"
                  />
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                      <FileText size={14} className="text-white/50" />
                      Anmerkungen (optional)
                    </label>
                    <textarea
                      data-testid="input-notes"
                      value={form.notes}
                      onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Besondere Anforderungen, Lieferadresse, etc."
                      rows={3}
                      className="w-full px-4 py-3 border border-[#1e3a5f] text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#0F1A2E] transition-colors resize-none"
                    />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right: Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-[#0A1220] p-6 md:p-8 border border-[#1e3a5f] sticky top-28"
              >
                <h3 className="font-heading text-xl font-semibold text-white mb-6">
                  Buchungsübersicht
                </h3>

                {/* Vehicle Image */}
                <div className="relative h-40 mb-6 overflow-hidden">
                  <img src={vehicle.image_url} alt={vehicle.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <Truck size={18} className="text-[#F5A623]" />
                  <span className="font-semibold text-white">{vehicle.name}</span>
                </div>

                <div className="space-y-3 mb-6 text-sm">
                  <div className="flex justify-between text-white/70">
                    <span>Tagespreis</span>
                    <span className="font-medium text-white">{vehicle.price_per_day.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between text-white/70">
                    <span>Miettage</span>
                    <span className="font-medium text-white">{totalDays > 0 ? totalDays : '—'}</span>
                  </div>
                  {startDate && endDate && (
                    <div className="flex justify-between text-white/70">
                      <span>Zeitraum</span>
                      <span className="font-medium text-white text-right text-xs">
                        {formatDisplayDate(startDate)}<br/>– {formatDisplayDate(endDate)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-t border-[#1e3a5f] pt-4 mb-6">
                  <div className="flex justify-between items-baseline">
                    <span className="text-base font-semibold text-white">Gesamtpreis</span>
                    <span className="text-2xl font-bold text-[#0F1A2E]">
                      {totalDays > 0 ? `${totalPrice}€` : '—'}
                    </span>
                  </div>
                  <p className="text-xs text-white/50 mt-1">inkl. Vollkasko-Versicherung</p>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 mb-4" data-testid="booking-error">
                    <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting || totalDays <= 0}
                  data-testid="submit-booking"
                  className="w-full bg-[#0F1A2E] text-white py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Wird verarbeitet...' : 'Zur Zahlung'}
                </button>

                <p className="text-xs text-white/50 text-center mt-4">
                  Sichere Zahlung über Stripe. Sie werden zur Zahlungsseite weitergeleitet.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// --- Helper Components ---

function FormField({ icon: Icon, label, type = 'text', value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
        <Icon size={14} className="text-white/50" />
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-[#1e3a5f] text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#0F1A2E] transition-colors"
      />
    </div>
  );
}

function formatDisplayDate(date) {
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// --- Calendar Component ---

function BookingCalendar({ currentMonth, setCurrentMonth, startDate, setStartDate, endDate, setEndDate, bookedRanges }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Monday = 0

  const days = [];
  for (let i = 0; i < startWeekday; i++) {
    days.push(null);
  }
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  const isBooked = (date) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return bookedRanges.some(range => dateStr >= range.start_date && dateStr < range.end_date);
  };

  const isPast = (date) => {
    if (!date) return false;
    return date < today;
  };

  const isInRange = (date) => {
    if (!date || !startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isStart = (date) => {
    if (!date || !startDate) return false;
    return date.getTime() === startDate.getTime();
  };

  const isEnd = (date) => {
    if (!date || !endDate) return false;
    return date.getTime() === endDate.getTime();
  };

  const handleDateClick = (date) => {
    if (!date || isPast(date) || isBooked(date)) return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(null);
      } else {
        // Check if any booked date is in between
        const hasBookedBetween = bookedRanges.some(range => {
          const rs = new Date(range.start_date);
          const re = new Date(range.end_date);
          return (rs > startDate && rs < date) || (re > startDate && re < date);
        });
        if (hasBookedBetween) {
          setStartDate(date);
          setEndDate(null);
        } else {
          setEndDate(date);
        }
      }
    }
  };

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  return (
    <div data-testid="booking-calendar">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={prevMonth}
          data-testid="calendar-prev-month"
          className="p-2 hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft size={20} className="text-[#0F1A2E]" />
        </button>
        <h3 className="font-heading text-lg font-semibold text-white">
          {monthNames[month]} {year}
        </h3>
        <button
          onClick={nextMonth}
          data-testid="calendar-next-month"
          className="p-2 hover:bg-gray-100 transition-colors"
        >
          <ChevronRight size={20} className="text-[#0F1A2E]" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 mb-2">
        {dayNames.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-white/50 uppercase tracking-wider py-2">
            {d}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7">
        {days.map((date, i) => {
          if (!date) return <div key={`empty-${i}`} className="py-3"></div>;

          const disabled = isPast(date) || isBooked(date);
          const inRange = isInRange(date);
          const start = isStart(date);
          const end = isEnd(date);
          const booked = isBooked(date);

          let className = "py-3 text-center text-sm cursor-pointer transition-colors relative ";

          if (disabled) {
            className += booked
              ? "bg-red-50 text-red-300 cursor-not-allowed line-through"
              : "text-gray-300 cursor-not-allowed";
          } else if (start || end) {
            className += "bg-[#0F1A2E] text-white font-semibold";
          } else if (inRange) {
            className += "bg-[#0F1A2E]/10 text-[#0F1A2E] font-medium";
          } else {
            className += "hover:bg-[#0F1A2E]/5 text-white";
          }

          return (
            <div
              key={i}
              data-testid={`calendar-day-${date.getDate()}`}
              className={className}
              onClick={() => !disabled && handleDateClick(date)}
            >
              {date.getDate()}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mt-6 text-xs text-white/50">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#0F1A2E]"></div>
          <span>Ausgewählt</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#0F1A2E]/10"></div>
          <span>Mietzeitraum</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-50 border border-red-200"></div>
          <span>Gebucht</span>
        </div>
      </div>
    </div>
  );
}
